import styled from "@emotion/styled";
import React, { CSSProperties, HTMLAttributes } from "react";
import { observer } from "mobx-react-lite";
import LeftBar from "./LeftBar";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import { Row } from "@components/Flex";
import MobileTabBar, { TAB_BAR_HEIGHT } from "@components/MobileTabBar";
import useIsMobile, { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";

interface IProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  extra?: JSX.Element | JSX.Element[] | null;
  subtitle?: string;
  leftBarStyle?: CSSProperties;
}

const Root = styled.div`
  display: flex;
  /* Subtract the customer notification banner's height (0px when hidden). */
  height: calc(100vh - var(--banner-height, 0px));
  width: 100%;
  align-items: stretch;
  max-width: 1920px;
  overflow: hidden;

  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    min-width: 1152px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: calc(var(--vh-full, 100vh) - var(--banner-height, 0px));
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
  padding: 24px 24px 32px 0;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    /* Clear the fixed tab bar: its full height (TAB_BAR_HEIGHT + bottom inset)
       plus 24px breathing room, so the last card scrolls fully above the bar. */
    padding: 16px 16px
      calc(${TAB_BAR_HEIGHT + 24}px + var(--tabbar-inset, env(safe-area-inset-bottom, 0px)))
      16px;
  }
`;

const Layout: React.FC<IProps> = ({
  extra,
  title,
  subtitle,
  leftBarStyle,
  children,
  ...rest
}) => {
  const isMobile = useIsMobile();

  return (
    <Root {...rest}>
      <LeftBar style={leftBarStyle} />
      <Content>
        {title && (
          <Row justifyContent="space-between">
            <Text
              type="heading"
              style={{ marginBottom: 14 }}
              size={24}
              color="#494C5B"
            >
              {title}
            </Text>
            {extra}
          </Row>
        )}
        {subtitle && (
          <Text
            style={{ marginBottom: 8, maxWidth: 800 }}
            size={15}
            color="#494C5B"
          >
            {subtitle}
          </Text>
        )}
        <SizedBox height={8} />
        {children}
      </Content>
      {isMobile && <MobileTabBar />}
    </Root>
  );
};
export default observer(Layout);
